import React from 'react';
import * as Yup from 'yup';
import { Button, Tabs } from '@economic/taco';
import { useCurrentAgreement } from '@economic/react-app';

import { Column, Row } from '../../shared/components/Grid/Grid';
import { Condition, Form, Field } from '../../shared/components/Form';
import AccessDropdown from '../../shared/components/ui/AccessDropdown';
import AccountComboBox from '../../shared/components/ui/AccountCombobox';
import AccountTypeDropdown from '../../shared/components/ui/AccountTypeDropdown';
import DebitCreditDropdown from '../../shared/components/ui/DebitCreditDropdown';
import VatCodeDropdown from '../../shared/components/ui/VatCodeDropdown';
import { FormProps } from '../types';
import internalApiClient from '../../shared/util/api/internalApiClient';
import KeyFigureCodeDropdown from '../../shared/components/ui/KeyFigureCodeDropdown';
import { t } from '../../shared/components/l10n/l10n';
import DepartmentCombobox from '../../shared/components/ui/DepartmentCombobox';
import UnitDropdown from '../../shared/components/ui/UnitDropdown';
import CurrencyDropdown from '../../shared/components/ui/CurrencyDropdown';

const isFieldDisabled = (name, values) => {
    switch (name) {
        case 'vatCode':
        case 'accounts.contraAccount':
        case 'accounts.realisationAccount':
        case 'debitCredit':
        case 'access':
        case 'isBlockedForDirectEntries':
        case 'currency':
            return values.type !== 1 && values.type !== 2;

        case 'accounts.openingAccount':
            return values.type !== 2;

        case 'accounts.totalFromAccount':
            return values.type !== 3;

        case 'keyFigureCode':
            return values.type !== 1 && values.type !== 2 && values.type !== 3 && values.type !== 6;

        case 'totalIntervals':
            return values.type !== 6;

        default:
            return false;
    }
};

interface Account {
    type: number;
    number: number;
    name: string;
    vatCode?: string;
    accounts?: {
        contraAccount?: number;
        totalFromAccount?: number;
        openingAccount?: number;
        realisationAccount?: number;
    };
    keyFigureCode?: number;
    debitCredit: boolean;
    access: boolean;
    isBlockedForDirectEntries: boolean;
    totalIntervals?: string;
    dimension?: {
        department?: number;
        isDepartmentMandatory?: boolean;
        isUnitMandatory?: boolean;
        unit1?: number;
        unit2?: number;
    };
    currency?: string;
}

const initialValues: Partial<Account> = {
    type: undefined,
    number: undefined,
    name: undefined,
    vatCode: undefined,
    accounts: {
        contraAccount: undefined,
        totalFromAccount: undefined,
        openingAccount: undefined,
        realisationAccount: undefined,
    },
    keyFigureCode: undefined,
    debitCredit: false,
    access: true,
    isBlockedForDirectEntries: false,
    totalIntervals: undefined,
    dimension: {
        department: undefined,
        isDepartmentMandatory: false,
        isUnitMandatory: false,
        unit1: undefined,
        unit2: undefined,
    },
    currency: undefined,
};

const validationSchema = Yup.object({
    type: Yup.number().required(),
    number: Yup.number().required(),
    name: Yup.string().required(),
    vatCode: Yup.string().nullable(),
    accounts: Yup.object({
        contraAccount: Yup.number().nullable(),
        totalFromAccount: Yup.number().nullable(),
        openingAccount: Yup.number().nullable(),
        realisationAccount: Yup.number().nullable(),
    }).nullable(),
    keyFigureCode: Yup.number().nullable(),
    debitCredit: Yup.boolean().required(),
    access: Yup.boolean().required(),
    isBlockedForDirectEntries: Yup.boolean().required(),
    totalIntervals: Yup.string(),
    dimension: Yup.object({
        department: Yup.number().nullable(),
        isDepartmentMandatory: Yup.boolean(),
        isUnitMandatory: Yup.boolean(),
        unit1: Yup.number().nullable(),
        unit2: Yup.number().nullable(),
    }).nullable(),
    currency: Yup.string(),
});

const getUpdateValue = (key, type, allValues) => (isFieldDisabled(key, { type }) ? undefined : allValues[key]);

const updater = [
    {
        field: 'type',
        updates: (type, _, allValues: any = {}) => {
            return {
                'accounts.contraAccount': getUpdateValue('accounts.contraAccount', type, allValues),
                'accounts.openingAccount': getUpdateValue('accounts.openingAccount', type, allValues),
                'accounts.totalFromAccount': getUpdateValue('accounts.totalFromAccount', type, allValues),
                'accounts.realisationAccount': getUpdateValue('accounts.realisationAccount', type, allValues),
                currency: getUpdateValue('currency', type, allValues),
                vatCode: getUpdateValue('vatCode', type, allValues),
            };
        },
    },
];

enum CreateAccountTab {
    General = 'tab-general',
    Dimension = 'tab-dimension',
}
interface AccountProps extends FormProps<Account> {
    modalDraggable?: boolean;
}
export default function CreateAccount({
    initialData = initialValues,
    notifications,
    onSubmitted,
    onSubmitError,
    onCancel,
    modalDraggable,
}: AccountProps) {
    const agreement = useCurrentAgreement();
    const hasDimensionModule = agreement.modules.dimensions;
    const hasAccrualModule = agreement.modules.accruals || agreement.modules.fixedAssetsAndAccruals;

    const handleSubmit = async values => {
        try {
            const response = await internalApiClient.post(`/accounts`, values);
            return Promise.resolve(response);
        } catch (error) {
            error.validation = error.response.data;
            return Promise.reject(error);
        }
    };

    return (
        <Form
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={onSubmitted}
            onSubmitError={onSubmitError}
            updates={updater}
            validationSchema={validationSchema}>
            {({ values }) => {
                const defaultFields = (
                    <Row divided>
                        <Column xs="3">
                            <Field.Dropdown
                                label={t`FORM_FIELD_LABEL_TYPE`}
                                name="type"
                                as={AccountTypeDropdown}
                                required
                                includeAllTypesButSumAlpha
                            />
                            <Field.Number label={t`FORM_FIELD_LABEL_NUMBER`} name="number" required />
                            <Field.Text label={t`FORM_FIELD_LABEL_NAME`} name="name" required />
                        </Column>

                        <Column xs="3">
                            <Field.Dropdown
                                label={t`FORM_FIELD_LABEL_VAT_ACCOUNT_CODE`}
                                name="vatCode"
                                disabled={isFieldDisabled('vatCode', values)}
                                as={VatCodeDropdown}
                                emptyValue={null}
                            />
                            <Field.Combobox
                                label={t`FORM_FIELD_LABEL_CONTRA_ACCOUNT_NUMBER`}
                                name="accounts.contraAccount"
                                disabled={isFieldDisabled('accounts.contraAccount', values)}
                                as={AccountComboBox}
                            />

                            <Field.Combobox
                                label={t`FORM_FIELD_LABEL_TOTAL_FROM_ACCOUNT`}
                                name="accounts.totalFromAccount"
                                disabled={isFieldDisabled('accounts.totalFromAccount', values)}
                                as={AccountComboBox}
                            />
                        </Column>

                        <Column xs="3">
                            <Field.Combobox
                                label={t`FORM_FIELD_LABEL_OPENING_ACCOUNT`}
                                name="accounts.openingAccount"
                                disabled={isFieldDisabled('accounts.openingAccount', values)}
                                as={AccountComboBox}
                            />
                            {hasAccrualModule && (
                                <Field.Combobox
                                    label={t`FORM_FIELD_LABEL_REALISATION_ACCOUNT`}
                                    name="accounts.realisationAccount"
                                    disabled={isFieldDisabled('accounts.realisationAccount', values)}
                                    as={AccountComboBox}
                                />
                            )}
                            <Field.Dropdown
                                label={t`FORM_FIELD_LABEL_KEY_FIGURE_CODE`}
                                name="keyFigureCode"
                                disabled={isFieldDisabled('keyFigureCode', values)}
                                as={KeyFigureCodeDropdown}
                                emptyValue={null}
                            />
                        </Column>

                        <Column xs="3">
                            <Row>
                                <Column xs="6">
                                    <Field.Dropdown
                                        label={t`FORM_FIELD_LABEL_DEBIT_CREDIT`}
                                        name="debitCredit"
                                        disabled={isFieldDisabled('debitCredit', values)}
                                        as={DebitCreditDropdown}
                                    />
                                </Column>
                                <Column xs="6">
                                    <Field.Dropdown
                                        label={t`FORM_FIELD_LABEL_ACCESS`}
                                        name="access"
                                        disabled={isFieldDisabled('access', values)}
                                        as={AccessDropdown}
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column>
                                    <Field.Checkbox
                                        text={t`FORM_FIELD_LABEL_BLOCK_DIRECT_ENTRIES`}
                                        name="isBlockedForDirectEntries"
                                        disabled={isFieldDisabled('isBlockedForDirectEntries', values)}
                                    />

                                    <Condition for="type" when={type => type === 1 || type === 2}>
                                        <Row>
                                            <Column xs="6">
                                                <Field.Dropdown
                                                    label={t`FORM_FIELD_LABEL_CURRENCY`}
                                                    name="currency"
                                                    as={CurrencyDropdown}
                                                />
                                            </Column>
                                        </Row>
                                    </Condition>

                                    <Condition for="type" when={type => type === 6}>
                                        <Field.Text
                                            label={t`FORM_FIELD_LABEL_TOTAL_INTERVALS`}
                                            name="totalIntervals"
                                            disabled={isFieldDisabled('totalIntervals', values)}
                                        />
                                    </Condition>
                                </Column>
                            </Row>
                        </Column>
                    </Row>
                );

                return (
                    <>
                        <h1>{t`FORM_ACCOUNTS_CREATETEXT`}</h1>

                        {hasDimensionModule ? (
                            <Tabs defaultId={CreateAccountTab.General}>
                                <Tabs.List>
                                    <Tabs.Trigger
                                        id={CreateAccountTab.General}>{t`FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL`}</Tabs.Trigger>
                                    <Tabs.Trigger
                                        id={CreateAccountTab.Dimension}>{t`FIREBIRD_FORM_COA_TAB_DIMENSION`}</Tabs.Trigger>
                                </Tabs.List>
                                <Tabs.Content id={CreateAccountTab.General}>{defaultFields}</Tabs.Content>
                                <Tabs.Content id={CreateAccountTab.Dimension}>
                                    <Row divided>
                                        <Column xs="3">
                                            <Field.Combobox
                                                label={t`FORM_FIELD_LABEL_DEPARTMENT_NUMBER`}
                                                name="dimension.department"
                                                modalDraggable={modalDraggable}
                                                as={DepartmentCombobox}
                                            />
                                            <Field.Checkbox
                                                text={t`FORM_FIELD_LABEL_DIMENSION_MANDATORY_DEPARTMENT`}
                                                name="dimension.isDepartmentMandatory"
                                            />
                                        </Column>
                                        <Column xs="3">
                                            <Field.Checkbox
                                                text={t`FORM_FIELD_LABEL_DIMENSION_MANDATORY_UNIT`}
                                                name="dimension.isUnitMandatory"
                                            />
                                            <Field.Dropdown
                                                label={t`FORM_FIELD_LABEL_DIMENSION_UNIT1`}
                                                name="dimension.unit1"
                                                as={UnitDropdown}
                                                emptyValue={null}
                                            />
                                            <Field.Dropdown
                                                label={t`FORM_FIELD_LABEL_DIMENSION_UNIT2`}
                                                name="dimension.unit2"
                                                as={UnitDropdown}
                                                emptyValue={null}
                                            />
                                        </Column>
                                    </Row>
                                </Tabs.Content>
                            </Tabs>
                        ) : (
                            defaultFields
                        )}

                        <div className="form__buttons">
                            <Button type="submit" appearance="primary">{t`BUTTON_SAVE`}</Button>
                            {onCancel && (
                                <Button onClick={onCancel} appearance="default">
                                    {t`BUTTON_CANCEL`}
                                </Button>
                            )}
                        </div>
                    </>
                );
            }}
        </Form>
    );
}
